import React, { useEffect, useState, Fragment, useRef } from "react";
import { PageHeader } from '../../../../Common';
import services from "../services";
import authService from "../../../Common/services";
import { LoadingSpinerComponent } from "../../../../Common/loader";
import { trackPromise } from "react-promise-tracker";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import FormHelperText from "@material-ui/core/FormHelperText";
import TextField from "@material-ui/core/TextField";
import Joi from "@hapi/joi";
import { QRTagBagWiseReportSchemaObject } from "../QRTagBagWiseReportSchema";
import { validateProperty, validate } from "../../../Common";
import tokenService from "../../../../Common/tokenDecoder";
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import ReactToPrint from "react-to-print";
import CreatePdf from './createPdf';
import '../../../Common/Table/table.css';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import MessageModal from '../../../../UI/Modal/MessageModal';

const screenCode = "QRTAGBAGWISEREPORT";

/* ------------------------------------------------------------------
   Helpers (outside the component so they are not re-created on render)
------------------------------------------------------------------- */

// Same bulk key logic used everywhere in this screen.
const bulkKeyOf = (row) =>
    row.bulkNo != null ? row.bulkNo : `__nobulk__GTN__${row.gtnNumber}`;

/**
 * Loss per kilo for ONE bulk (matches the Excel: L5 = (M8 - H8) / H8)
 *   facWt      = factory bulk weight        (Orig bulk Fac wt)
 *   fieldWtSum = sum of field bag weights   (Orig Field wt)
 * Returns a number or null when it cannot be calculated.
 */
function calcDifferencePerKilo(facWt, fieldWtSum) {
    const fac = facWt != null ? parseFloat(facWt) : NaN;
    const field = fieldWtSum != null ? parseFloat(fieldWtSum) : NaN;
    if (isNaN(fac) || isNaN(field) || field <= 0) return null;
    return (fac - field) / field;
}

/**
 * Stamps every row with the bulk-wise values so the grid, the PDF and the
 * Excel export all show the same numbers (same as the Excel sheet):
 *
 *   differencePerKilo = (bulk factory wt - bulk field wt sum) / bulk field wt sum   (L5)
 *   origFacWt (bag)   = field bag wt + differencePerKilo * field bag wt            (R column)
 *
 * Rows that have no field bag inside a calculated bulk get no bag-wise factory wt,
 * so the sum of the bag-wise values of a bulk equals the bulk factory weight.
 */
function applyBulkWeights(rows) {
    const fieldWtSums = {};
    rows.forEach((row) => {
        const key = bulkKeyOf(row);
        fieldWtSums[key] = (fieldWtSums[key] || 0) + parseFloat(row.origFieldWt || 0);
    });

    return rows.map((row) => {
        const diff = calcDifferencePerKilo(row.origBulkFacWt, fieldWtSums[bulkKeyOf(row)]);
        const next = { ...row, differencePerKilo: diff };

        if (diff != null) {
            const fieldWt = row.fieldBagNo != null ? parseFloat(row.origFieldWt || 0) : null;
            const bagFacWt = fieldWt != null ? fieldWt + (diff * fieldWt) : null;
            next.origFacWt = bagFacWt;
            next.displayOrigFacWt = bagFacWt;
        }
        return next;
    });
}

const fmt2 = (v) =>
    v != null && !isNaN(parseFloat(v)) ? parseFloat(v).toFixed(2) : "-";

const getDisplayOrigFacWt = (row) =>
    fmt2(row.displayOrigFacWt != null ? row.displayOrigFacWt : row.origFacWt);

/**
 * Single source of truth for the table layout.
 * Used by BOTH the on-screen table and the Excel export.
 */
function buildDisplayRows(sortedReportData, fieldRunSpanMap, fieldTripletSpanMap) {
    const rowSpanCounts = {};
    const bulkFieldWtSums = {};
    sortedReportData.forEach((row) => {
        const k = bulkKeyOf(row);
        rowSpanCounts[k] = (rowSpanCounts[k] || 0) + 1;
        bulkFieldWtSums[k] = (bulkFieldWtSums[k] || 0) + parseFloat(row.origFieldWt || 0);
    });

    const bulkOrder = [];
    const seen = new Set();
    const bulkGtn = {};
    const bulkIsReal = {};
    sortedReportData.forEach((row) => {
        const k = bulkKeyOf(row);
        if (!seen.has(k)) {
            seen.add(k);
            bulkOrder.push(k);
            bulkGtn[k] = row.gtnNumber;
            bulkIsReal[k] = row.bulkNo != null;
        }
    });

    const gtnRunStartOfBulk = {};
    const gtnRunSpan = {};
    const bulkNumberMap = {};
    let bi = 0;
    while (bi < bulkOrder.length) {
        const runStart = bulkOrder[bi];
        const gtnVal = bulkGtn[runStart];
        let total = rowSpanCounts[runStart];
        let counter = 1;
        if (bulkIsReal[runStart]) bulkNumberMap[runStart] = counter++;
        let bj = bi + 1;
        while (bj < bulkOrder.length && bulkGtn[bulkOrder[bj]] != null && bulkGtn[bulkOrder[bj]] === gtnVal) {
            gtnRunStartOfBulk[bulkOrder[bj]] = runStart;
            if (bulkIsReal[bulkOrder[bj]]) bulkNumberMap[bulkOrder[bj]] = counter++;
            total += rowSpanCounts[bulkOrder[bj]];
            bj++;
        }
        gtnRunStartOfBulk[runStart] = runStart;
        gtnRunSpan[runStart] = total;
        bi = bj;
    }

    const renderedBulks = new Set();
    const shownGtnValues = new Set();

    return sortedReportData.map((row) => {
        const bulkKey = bulkKeyOf(row);
        const isFirstOfBulk = !renderedBulks.has(bulkKey);
        renderedBulks.add(bulkKey);

        const gtnStart = gtnRunStartOfBulk[bulkKey];
        const isFirstOfGtnRun = isFirstOfBulk && gtnStart === bulkKey;

        let gtnCellText = row.gtnNumber;
        if (isFirstOfGtnRun && row.bulkNo != null) {
            if (shownGtnValues.has(row.gtnNumber)) gtnCellText = "";
            else shownGtnValues.add(row.gtnNumber);
        }

        const fieldWtSum = bulkFieldWtSums[bulkKey] || 0;
        const bulkDiff = calcDifferencePerKilo(row.origBulkFacWt, fieldWtSum);

        return {
            row,
            isFirstOfBulk,
            span: rowSpanCounts[bulkKey],
            isFirstOfFieldRun: fieldRunSpanMap.has(row),
            fieldSpan: fieldRunSpanMap.get(row) || 1,
            showFieldTriplet: fieldTripletSpanMap.has(row),
            tripletSpan: fieldTripletSpanMap.get(row) || 1,
            bulkNoText: bulkNumberMap[bulkKey] != null ? bulkNumberMap[bulkKey] : "-",
            bulkFacWtText: fmt2(row.origBulkFacWt),
            fieldWtSumText: fieldWtSum.toFixed(2),
            bulkDiffText: bulkDiff != null ? bulkDiff.toFixed(2) : "-",
            isFirstOfGtnRun,
            gtnSpan: gtnRunSpan[gtnStart],
            gtnCellText,
            perRowFactory: row.facWeighingEventCount > 1,
            facTimeText: row.facTime != null ? row.facTime : "-",
        };
    });
}

export function QRTagBagWiseReportListing(props) {

    const componentRef = useRef();

    const [groups, setGroups] = useState([]);
    const [estates, setEstates] = useState([]);
    const [multipleEstates, setMultipleEstates] = useState([]);
    const [division, setDivision] = useState([]);
    const sessionNumbers = { 1: "Session 1", 2: "Session 2", 3: "Session 3" };
    const [isTableHide, setIsTableHide] = useState(true);
    const [noDataWarning, setNoDataWarning] = useState(false);
    const [qrTagBagWiseReportDetails, setQrTagBagWiseReportDetails] = useState({
        groupID: "0",
        estateID: "0",
        divisionID: "0",
        gtnNumber: "0",
        sessionNo: "0",
        date: new Date().toISOString().split('T')[0]
    });

    const [selectedSearchValues, setSelectedSearchValues] = useState({
        group: "0",
        estate: "0",
        division: "0",
        gtnNumber: "0",
        sessionNo: "0",
        date: "",
    });

    const [messageModel, setMessageModel] = useState({
        hidden: true,
        type: "",
        description: "",
    });
    const [errors, setErrors] = useState({});
    const [permissionList, setPermissions] = useState({
        isAddEditEnabled: false,
        isGroupFilterEnabled: false,
        isOperationEntityFilterEnabled: false,
    });

    const schema = Joi.object(QRTagBagWiseReportSchemaObject).options({ allowUnknown: true });

    const [reportData, setReportData] = useState([]);

    useEffect(() => {
        trackPromise(getPermissions());
    }, []);

    useEffect(() => {
        trackPromise(getEstatesForDropdown());
    }, [qrTagBagWiseReportDetails.groupID]);

    useEffect(() => {
        trackPromise(getDivisionForDropdown());
    }, [qrTagBagWiseReportDetails.estateID]);

    function handleGroupChange(target) {
        validateInputs(target);
        setQrTagBagWiseReportDetails({
            ...qrTagBagWiseReportDetails,
            groupID: target.value,
            estateID: "0",
            divisionID: "0",
            gtnNumber: "0",
            date: new Date().toISOString().split('T')[0],
        });
        clearTable();
    }

    function handleEstateChange(target) {
        validateInputs(target);
        setQrTagBagWiseReportDetails({
            ...qrTagBagWiseReportDetails,
            estateID: target.value,
            divisionID: "0",
            gtnNumber: "0",
            date: new Date().toISOString().split('T')[0],
        });
        clearTable();
    }

    function handleChangeDivision(target) {
        validateInputs(target);
        setQrTagBagWiseReportDetails({
            ...qrTagBagWiseReportDetails,
            divisionID: target.value,
            gtnNumber: "0",
            date: new Date().toISOString().split('T')[0],
        });
        clearTable();
    }

    function handleChangeCommon(target) {
        validateInputs(target);
        setQrTagBagWiseReportDetails({
            ...qrTagBagWiseReportDetails,
            [target.name]: target.value
        });
        clearTable();
    }

    function validateInputs(target) {
        const error = { ...errors };
        const errorMessage = validateProperty(target, QRTagBagWiseReportSchemaObject);
        errorMessage
            ? (error[target.name] = errorMessage)
            : delete error[target.name];
        setErrors(error);
    }

    async function getPermissions() {
        var permissions = await authService.getPermissionsByScreen(screenCode);

        var isAuthorized = permissions.find(p => p.permissionCode == 'VIEWQRTAGBAGWISEREPORT');
        if (isAuthorized === undefined) {
            props.history.push('/unauthorized');
        }

        var isGroupFilterEnabled = permissions.find(p => p.permissionCode == 'GROUPDROPDOWN');
        var isOperationEntityFilterEnabled = permissions.find(p => p.permissionCode == 'OPERATIONENTITYDROPDOWN');

        setPermissions({
            ...permissionList,
            isGroupFilterEnabled: isGroupFilterEnabled !== undefined,
            isOperationEntityFilterEnabled: isOperationEntityFilterEnabled !== undefined,
        });

        var token = tokenService.getUserDetailsFromTokenV2();
        setQrTagBagWiseReportDetails({
            ...qrTagBagWiseReportDetails,
            groupID: token.groupID,
            estateID: token.operationEntityID
        });

        trackPromise(getGroupsForDropdown());
    }

    async function getGroupsForDropdown() {
        const result = await services.getAlGroupsForDropdown();
        setGroups(result);
    }

    async function getEstatesForDropdown() {
        const data = await services.getEstatesByGroupID(qrTagBagWiseReportDetails.groupID);
        const token = tokenService.getUserDetailsFromTokenV2();
        const validEstateIds = token.operationEntities.map(entity => String(entity.OperationEntityID));

        const estateList = data
            .map((name, index) => name ? { id: String(index), name } : null)
            .filter(item => item !== null);

        const filteredEstates = estateList.filter(estate =>
            validEstateIds.includes(String(estate.id))
        );

        const filteredEstateArray = [];
        filteredEstates.forEach(estate => {
            filteredEstateArray[parseInt(estate.id)] = estate.name;
        });

        setMultipleEstates(filteredEstateArray);
        setEstates(filteredEstateArray);
    }

    async function getDivisionForDropdown() {
        const data = await services.GetDivisionByEstateID(qrTagBagWiseReportDetails.estateID);
        setDivision(data);
    }

    function splitCommaSeparatedValue(value) {
        if (value == null || value === "") return [];
        return String(value)
            .split(',')
            .map(item => item.trim())
            .filter(item => item !== "");
    }

    function getSplitValue(value, index, totalCount) {
        const values = splitCommaSeparatedValue(value);
        if (values.length === totalCount) return values[index];
        return index === 0 ? value : null;
    }

    function expandFactoryTagRows(rows) {
        if (!Array.isArray(rows)) return [];

        const expandedRows = [];
        const bulkGroupsMap = new Map();

        rows.forEach((row) => {
            const bKey = bulkKeyOf(row);
            if (!bulkGroupsMap.has(bKey)) bulkGroupsMap.set(bKey, []);
            bulkGroupsMap.get(bKey).push(row);
        });

        bulkGroupsMap.forEach((rowsInBulk) => {
            const factorySourceRow = rowsInBulk.find(row => splitCommaSeparatedValue(row.facBagNo).length > 1);

            if (!factorySourceRow) {
                rowsInBulk.forEach(row => expandedRows.push(row));
                return;
            }

            const factoryTags = splitCommaSeparatedValue(factorySourceRow.facBagNo);
            const rowCount = Math.max(rowsInBulk.length, factoryTags.length);

            for (let index = 0; index < rowCount; index++) {
                const sourceRow = rowsInBulk[index] || factorySourceRow;
                const origFacWt = getSplitValue(factorySourceRow.origFacWt, index, factoryTags.length);
                expandedRows.push({
                    ...sourceRow,
                    fieldBagNo: rowsInBulk[index] ? rowsInBulk[index].fieldBagNo : null,
                    origFieldWt: rowsInBulk[index] ? rowsInBulk[index].origFieldWt : null,
                    fieldTime: rowsInBulk[index] ? rowsInBulk[index].fieldTime : null,
                    facBagNo: factoryTags[index] || null,
                    origFacWt: origFacWt,
                    displayOrigFacWt: origFacWt != null ? origFacWt : factorySourceRow.origFacWt,
                    facTime: getSplitValue(factorySourceRow.facTime, index, factoryTags.length) || factorySourceRow.facTime,
                    facWeighingEventCount: Math.max(factorySourceRow.facWeighingEventCount || 0, factoryTags.length)
                });
            }
        });

        // Bulk-wise loss per kilo and bag-wise factory weight (Excel logic)
        return applyBulkWeights(expandedRows);
    }

    function generateDropDownMenu(data) {
        let items = [];
        if (data != null) {
            for (const [key, value] of Object.entries(data)) {
                items.push(<MenuItem key={key} value={key}>{value}</MenuItem>);
            }
        }
        return items;
    }

    async function handleSearch(e) {
        e.preventDefault();
        setIsTableHide(true);
        setNoDataWarning(false);

        const errorMessages = validate(schema, qrTagBagWiseReportDetails);
        errorMessages !== null ? setErrors(errorMessages) : setErrors({});
        if (errorMessages) return;

        const result = await services.getQRTagBagWiseReportData(qrTagBagWiseReportDetails);

        if (!result || result.length === 0) {
            setReportData([]);
            setIsTableHide(true);
            setMessageModel({
                ...messageModel,
                hidden: false,
                type: "Warning",
                description: "No records found."
            });
            setTimeout(() => {
                modalDismissHandler();
            }, 3000);
            return;
        }

        const expandedResult = expandFactoryTagRows(result);

        setIsTableHide(false);
        setNoDataWarning(false);
        addSelectedSearchValues();
        setReportData(expandedResult);
    }

    function addSelectedSearchValues() {
        setSelectedSearchValues({
            ...selectedSearchValues,
            group: groups[qrTagBagWiseReportDetails.groupID],
            estate: estates[qrTagBagWiseReportDetails.estateID],
            division: qrTagBagWiseReportDetails.divisionID === "0" ? "0" : division[qrTagBagWiseReportDetails.divisionID],
            sessionNo: qrTagBagWiseReportDetails.sessionNo === "0" ? "0" : sessionNumbers[qrTagBagWiseReportDetails.sessionNo],
            date: qrTagBagWiseReportDetails.date
        });
    }

    /* ------------------------------------------------------------------
       Excel export - same headers, order, merges, values and colours
       as the on-screen table (uses displayRows).
       NOTE: totals, facTagCounts, fieldTagCounts and displayRows are
       defined below in the component body; createFile only runs on click
       so it can safely use them.
    ------------------------------------------------------------------- */
    async function createFile() {
        const wb = new ExcelJS.Workbook();
        const ws = wb.addWorksheet('QR Tag Bag Wise Report');
        const COLS = 16;

        const widths = [18, 12, 18, 10, 14, 10, 12, 10, 14, 12, 14, 16, 16, 12, 16, 20];
        widths.forEach((w, i) => (ws.getColumn(i + 1).width = w));

        const put = (r, c, value, rSpan = 1, cSpan = 1, opts = {}) => {
            const cell = ws.getCell(r, c);
            cell.value = value;
            if (rSpan > 1 || cSpan > 1) ws.mergeCells(r, c, r + rSpan - 1, c + cSpan - 1);
            if (opts.align) cell.alignment = { horizontal: opts.align, vertical: 'middle', wrapText: true };
            if (opts.bold) cell.font = { bold: true };
            if (opts.fill) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: opts.fill } };
        };

        // ---------- Header (same labels as the grid) ----------
        ["Collection Point", "Session", "Operator", "Field No"].forEach((t, i) =>
            put(1, i + 1, t, 2, 1, { bold: true, align: 'left' }));
        put(1, 5, "Field Process", 1, 3, { bold: true, align: 'center', fill: 'FF90EE90' });
        put(1, 8, "Factory Process", 1, 9, { bold: true, align: 'center', fill: 'FF8CA3C7' });

        const sub = [
            ["Bag No", 'left'], ["Orig wt", 'right'], ["Time", 'left'],
            ["Bulk No", 'left'], ["Orig bulk Fac wt", 'right'], ["Orig Field wt", 'right'],
            ["Diffrence per Kilo", 'right'], ["Bag No", 'left'], ["Orig Fac wt bag wise", 'right'],
            ["Time", 'left'], ["GTN/GRN No", 'left'], ["Remark", 'left'],
        ];
        sub.forEach(([t, a], i) => put(2, i + 5, t, 1, 1, { bold: true, align: a }));

        // ---------- Body (same spans as the grid) ----------
        const DUP = 'FFFFCDD2';
        let r = 3;
        displayRows.forEach((d) => {
            const row = d.row;

            if (d.isFirstOfFieldRun) {
                put(r, 1, row.divisionName, d.fieldSpan, 1, { align: 'left' });
                put(r, 2, row.sessionName, d.fieldSpan, 1, { align: 'left' });
                put(r, 3, row.operatorName, d.fieldSpan, 1, { align: 'left' });
                put(r, 4, row.fieldNo, d.fieldSpan, 1, { align: 'left' });
            }
            if (d.showFieldTriplet) {
                const dup = row.fieldBagNo != null && fieldTagCounts[row.fieldBagNo] > 1;
                put(r, 5, row.fieldBagNo, d.tripletSpan, 1, { align: 'left', bold: dup, fill: dup ? DUP : undefined });
                put(r, 6, row.origFieldWt, d.tripletSpan, 1, { align: 'right' });
                put(r, 7, row.fieldTime, d.tripletSpan, 1, { align: 'left' });
            }
            if (d.isFirstOfBulk) {
                put(r, 8, d.bulkNoText, d.span, 1, { align: 'left' });
                put(r, 9, d.bulkFacWtText, d.span, 1, { align: 'right' });
                put(r, 10, d.fieldWtSumText, d.span, 1, { align: 'right' });
                put(r, 11, d.bulkDiffText, d.span, 1, { align: 'right' });
                put(r, 16, row.remark, d.span, 1, { align: 'left' });
            }

            const facDup = row.facBagNo != null && facTagCounts[row.facBagNo] > 1;
            const facOpts = { align: 'left', bold: facDup, fill: facDup ? DUP : undefined };
            if (d.perRowFactory) {
                put(r, 12, row.facBagNo, 1, 1, facOpts);
                put(r, 14, d.facTimeText, 1, 1, { align: 'left' });
            } else if (d.isFirstOfBulk) {
                put(r, 12, row.facBagNo, d.span, 1, facOpts);
                put(r, 14, d.facTimeText, d.span, 1, { align: 'left' });
            }
            put(r, 13, getDisplayOrigFacWt(row), 1, 1, { align: 'right' });

            if (d.isFirstOfGtnRun) put(r, 15, d.gtnCellText, d.gtnSpan, 1, { align: 'left' });

            r++;
        });

        // ---------- Total row (same as the grid footer) ----------
        put(r, 1, "Total", 1, 4, { bold: true, align: 'center' });
        put(r, 5, totals.fieldBagCount, 1, 1, { bold: true, align: 'right' });
        put(r, 6, parseFloat(totals.origFieldWt).toFixed(2), 1, 1, { bold: true, align: 'right' });
        put(r, 12, totals.facBagCount, 1, 1, { bold: true, align: 'right' });
        put(r, 13, parseFloat(totals.origFacWt).toFixed(2), 1, 1, { bold: true, align: 'right' });
        const lastRow = r;

        // ---------- Borders + vertical alignment on every cell ----------
        const thin = { style: 'thin', color: { argb: 'FFBDBDBD' } };
        for (let rr = 1; rr <= lastRow; rr++) {
            for (let cc = 1; cc <= COLS; cc++) {
                const cell = ws.getCell(rr, cc);
                cell.border = { top: thin, left: thin, bottom: thin, right: thin };
                cell.alignment = { vertical: 'middle', wrapText: true, ...(cell.alignment || {}) };
            }
        }

        ws.views = [{ state: 'frozen', ySplit: 2 }];

        const buffer = await wb.xlsx.writeBuffer();
        saveAs(
            new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
            'QRTagBagWiseReport.xlsx'
        );
    }

    function onClearClick() {
        clearState();
    }

    function clearState() {
        var token = tokenService.getUserDetailsFromTokenV2();
        setQrTagBagWiseReportDetails({
            ...qrTagBagWiseReportDetails,
            estateID: token.operationEntityID,
            divisionID: "0",
            gtnNumber: "0",
            sessionNo: "0",
            date: new Date().toISOString().split('T')[0],
        });
        setIsTableHide(true);
        setNoDataWarning(false);
        setErrors({});
    }

    function clearTable() {
        setIsTableHide(true);
        setNoDataWarning(false);
        setReportData([]);
    }

    function modalDismissHandler() {
        setMessageModel({ ...messageModel, hidden: true });
    }

    const totals = reportData.reduce((acc, row) => {
        if (row.fieldBagNo != null) acc.fieldBagCount += 1;
        acc.origFieldWt += parseFloat(row.origFieldWt || 0);
        if (row.facBagNo != null) acc.facBagCount += 1;
        acc.origFacWt += parseFloat(row.origFacWt || 0);
        return acc;
    }, { fieldBagCount: 0, origFieldWt: 0, facBagCount: 0, origFacWt: 0 });

    const fieldGroupKey = (row) => `${row.divisionName}|${row.sessionName}|${row.operatorName}|${row.fieldNo}`;
    const effectiveBulkKey = bulkKeyOf;

    const sortedReportData = [];
    const fieldTripletSpanMap = new Map();

    {
        const bulkGroupsMap = new Map();
        reportData.forEach((row) => {
            const bKey = effectiveBulkKey(row);
            if (!bulkGroupsMap.has(bKey)) bulkGroupsMap.set(bKey, []);
            bulkGroupsMap.get(bKey).push(row);
        });

        bulkGroupsMap.forEach((rowsInBulk) => {
            const fieldSubGroupsMap = new Map();
            rowsInBulk.forEach((row) => {
                const fKey = fieldGroupKey(row);
                if (!fieldSubGroupsMap.has(fKey)) fieldSubGroupsMap.set(fKey, []);
                fieldSubGroupsMap.get(fKey).push(row);
            });

            fieldSubGroupsMap.forEach((rowsInField) => {
                const fieldRows = rowsInField.filter(r => r.fieldBagNo != null);
                const factoryOnlyRows = rowsInField.filter(r => r.fieldBagNo == null);
                const actualCount = fieldRows.length;
                const extraCount = factoryOnlyRows.length;

                if (actualCount === 0) {
                    factoryOnlyRows.forEach((row, idx) => {
                        sortedReportData.push(row);
                        if (idx === 0) fieldTripletSpanMap.set(row, factoryOnlyRows.length || 1);
                    });
                    return;
                }

                let factoryIdx = 0;
                fieldRows.forEach((fRow, i) => {
                    const rowRank = i + 1;
                    const baseSpan = Math.floor(extraCount / actualCount);
                    const remainder = extraCount % actualCount;
                    const getsExtraRow = rowRank > (actualCount - remainder);
                    const ownCount = baseSpan + (getsExtraRow ? 1 : 0);
                    const runLength = 1 + ownCount;

                    sortedReportData.push(fRow);
                    fieldTripletSpanMap.set(fRow, runLength);

                    for (let k = 0; k < ownCount && factoryIdx < factoryOnlyRows.length; k++) {
                        sortedReportData.push(factoryOnlyRows[factoryIdx]);
                        factoryIdx++;
                    }
                });

                while (factoryIdx < factoryOnlyRows.length) {
                    sortedReportData.push(factoryOnlyRows[factoryIdx]);
                    factoryIdx++;
                }
            });
        });
    }

    const fieldRunSpanMap = new Map();
    {
        let i = 0;
        while (i < sortedReportData.length) {
            const key = fieldGroupKey(sortedReportData[i]);
            let j = i + 1;
            while (j < sortedReportData.length && fieldGroupKey(sortedReportData[j]) === key) j++;
            fieldRunSpanMap.set(sortedReportData[i], j - i);
            i = j;
        }
    }

    const facTagCounts = {};
    const fieldTagCounts = {};
    sortedReportData.forEach((row) => {
        if (row.facBagNo) facTagCounts[row.facBagNo] = (facTagCounts[row.facBagNo] || 0) + 1;
        if (row.fieldBagNo) fieldTagCounts[row.fieldBagNo] = (fieldTagCounts[row.fieldBagNo] || 0) + 1;
    });
    const isDupFacTag = (tag) => tag != null && facTagCounts[tag] > 1;
    const isDupFieldTag = (tag) => tag != null && fieldTagCounts[tag] > 1;

    // Shared layout used by BOTH the grid below and the Excel export
    const displayRows = buildDisplayRows(sortedReportData, fieldRunSpanMap, fieldTripletSpanMap);

    return (
        <Fragment>
            <LoadingSpinerComponent />
            <div className="row">
                <div className="col-md-12">
                    <div className="card">

                        <PageHeader
                            title="QR Tag Bag Wise Report"
                            isButtonEnable={permissionList.isAddEditEnabled}
                        />
                        <div className="card-body">
                            <form onSubmit={(e) => handleSearch(e)}>
                                <div className="form-group row">
                                    <div className="col-md">
                                        <InputLabel shrink id="groupID">Group *</InputLabel>
                                        <Select
                                            fullWidth
                                            labelId="groupID"
                                            id="groupID"
                                            name="groupID"
                                            value={qrTagBagWiseReportDetails.groupID}
                                            disabled={!permissionList.isGroupFilterEnabled}
                                            onChange={(e) => handleGroupChange(e.target)}
                                        >
                                            <MenuItem value="0">--Select Group--</MenuItem>
                                            {generateDropDownMenu(groups)}
                                        </Select>
                                        {errors["groupID"] && (<FormHelperText error>{errors["groupID"]}</FormHelperText>)}
                                    </div>
                                    <div className="col-md">
                                        <InputLabel shrink id="estateID">Operation Entity *</InputLabel>
                                        <Select
                                            fullWidth
                                            labelId="estateID"
                                            id="estateID"
                                            name="estateID"
                                            value={qrTagBagWiseReportDetails.estateID}
                                            disabled={!permissionList.isOperationEntityFilterEnabled}
                                            onChange={(e) => handleEstateChange(e.target)}
                                        >
                                            <MenuItem value="0">--Select Operation Entity--</MenuItem>
                                            {generateDropDownMenu(multipleEstates.length > 0 ? multipleEstates : estates)}
                                        </Select>
                                        {errors["estateID"] && (<FormHelperText error>{errors["estateID"]}</FormHelperText>)}
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-md">
                                        <InputLabel shrink id="divisionID">Collection Point</InputLabel>
                                        <Select
                                            fullWidth
                                            labelId="divisionID"
                                            id="divisionID"
                                            name="divisionID"
                                            value={qrTagBagWiseReportDetails.divisionID}
                                            onChange={(e) => handleChangeDivision(e.target)}
                                        >
                                            <MenuItem value="0">--All Collection Points--</MenuItem>
                                            {generateDropDownMenu(division)}
                                        </Select>
                                        {errors["divisionID"] && (<FormHelperText error>{errors["divisionID"]}</FormHelperText>)}
                                    </div>
                                    <div className="col-md">
                                        <InputLabel shrink id="date">Date *</InputLabel>
                                        <TextField
                                            fullWidth
                                            id="date"
                                            name="date"
                                            type="date"
                                            value={qrTagBagWiseReportDetails.date}
                                            onChange={(e) => handleChangeCommon(e.target)}
                                            InputLabelProps={{ shrink: true }}
                                            inputProps={{ max: new Date().toISOString().split('T')[0] }}
                                        />
                                        {errors["date"] && (<FormHelperText error>{errors["date"]}</FormHelperText>)}
                                    </div>
                                </div>
                                <div className="card-footer">
                                    <input
                                        disabled={validate(schema, qrTagBagWiseReportDetails)}
                                        type="button"
                                        className="btn btn-primary"
                                        value="Search"
                                        onClick={e => trackPromise(handleSearch(e))}
                                    />
                                    <input
                                        type="reset"
                                        className="btn btn-warning pull-right"
                                        value="Clear"
                                        onClick={onClearClick}
                                    />
                                </div>
                            </form>
                            {isTableHide ? null :
                                <div>
                                    <div className="row">
                                        <div className="col-md-12 pt-2 pb-3">
                                            <div className="card p-2">
                                                <div>
                                                    <Table
                                                        id="qrTagBagWiseReportTable"
                                                        aria-label="simple table"
                                                        style={{ display: "block", overflowX: "auto", whiteSpace: "nowrap", maxWidth: "1400px", borderColor: "white" }}>
                                                        <TableHead>
                                                            <TableRow>
                                                                <TableCell rowSpan={2} align="left" style={{ fontWeight: "bold" }}>Collection Point</TableCell>
                                                                <TableCell rowSpan={2} align="left" style={{ fontWeight: "bold" }}>Session</TableCell>
                                                                <TableCell rowSpan={2} align="left" style={{ fontWeight: "bold" }}>Operator</TableCell>
                                                                <TableCell rowSpan={2} align="left" style={{ fontWeight: "bold" }}>Field No</TableCell>
                                                                <TableCell colSpan={3} align="center" style={{ background: "lightgreen", fontWeight: "bold" }}>Field Process</TableCell>
                                                                <TableCell colSpan={9} align="center" style={{ background: "#8ca3c7", fontWeight: "bold" }}>Factory Process</TableCell>
                                                            </TableRow>
                                                            <TableRow>
                                                                {/* Field Process sub-columns */}
                                                                <TableCell align="left" style={{ fontWeight: "bold" }}>Bag No</TableCell>
                                                                <TableCell align="right" style={{ fontWeight: "bold" }}>Orig wt</TableCell>
                                                                <TableCell align="left" style={{ fontWeight: "bold" }}>Time</TableCell>

                                                                {/* Factory Process sub-columns */}
                                                                <TableCell align="left" style={{ fontWeight: "bold" }}>Bulk No</TableCell>
                                                                <TableCell align="right" style={{ fontWeight: "bold" }}>Orig bulk Fac wt</TableCell>
                                                                <TableCell align="right" style={{ fontWeight: "bold" }}>Orig Field wt</TableCell>
                                                                <TableCell align="right" style={{ fontWeight: "bold" }}>Diffrence per Kilo</TableCell>
                                                                <TableCell align="left" style={{ fontWeight: "bold" }}>Bag No</TableCell>
                                                                <TableCell align="right" style={{ fontWeight: "bold" }}>Orig Fac wt bag wise</TableCell>
                                                                <TableCell align="left" style={{ fontWeight: "bold" }}>Time</TableCell>
                                                                <TableCell align="left" style={{ fontWeight: "bold" }}>GTN/GRN No</TableCell>
                                                                <TableCell align="left" style={{ fontWeight: "bold" }}>Remark</TableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {displayRows.map((d, index) => {
                                                                const row = d.row;
                                                                const fieldDup = isDupFieldTag(row.fieldBagNo);
                                                                const facDup = isDupFacTag(row.facBagNo);
                                                                const dupStyle = { background: "#ffcdd2", fontWeight: "bold" };
                                                                const dupTitle = "Duplicate QR code in this report";
                                                                return (
                                                                    <TableRow key={index}>
                                                                        {d.isFirstOfFieldRun && <>
                                                                            <TableCell rowSpan={d.fieldSpan} component="th" scope="row">{row.divisionName}</TableCell>
                                                                            <TableCell rowSpan={d.fieldSpan} component="th" scope="row">{row.sessionName}</TableCell>
                                                                            <TableCell rowSpan={d.fieldSpan} component="th" scope="row">{row.operatorName}</TableCell>
                                                                            <TableCell rowSpan={d.fieldSpan} component="th" scope="row">{row.fieldNo}</TableCell>
                                                                        </>}
                                                                        {d.showFieldTriplet && <>
                                                                            <TableCell rowSpan={d.tripletSpan} component="th" scope="row"
                                                                                style={fieldDup ? dupStyle : undefined}
                                                                                title={fieldDup ? dupTitle : undefined}>{row.fieldBagNo}</TableCell>
                                                                            <TableCell rowSpan={d.tripletSpan} component="th" align="right" scope="row">{row.origFieldWt}</TableCell>
                                                                            <TableCell rowSpan={d.tripletSpan} component="th" scope="row">{row.fieldTime}</TableCell>
                                                                        </>}
                                                                        {d.isFirstOfBulk && <>
                                                                            <TableCell rowSpan={d.span} component="th" scope="row">{d.bulkNoText}</TableCell>
                                                                            <TableCell rowSpan={d.span} component="th" align="right" scope="row">{d.bulkFacWtText}</TableCell>
                                                                            <TableCell rowSpan={d.span} component="th" align="right" scope="row">{d.fieldWtSumText}</TableCell>
                                                                            <TableCell rowSpan={d.span} component="th" align="right" scope="row">{d.bulkDiffText}</TableCell>
                                                                        </>}
                                                                        {d.perRowFactory ? (
                                                                            <TableCell component="th" scope="row"
                                                                                style={facDup ? dupStyle : undefined}
                                                                                title={facDup ? dupTitle : undefined}>{row.facBagNo}</TableCell>
                                                                        ) : d.isFirstOfBulk && (
                                                                            <TableCell rowSpan={d.span} component="th" scope="row"
                                                                                style={facDup ? dupStyle : undefined}
                                                                                title={facDup ? dupTitle : undefined}>{row.facBagNo}</TableCell>
                                                                        )}
                                                                        <TableCell component="th" align="right" scope="row">{getDisplayOrigFacWt(row)}</TableCell>
                                                                        {d.perRowFactory ? (
                                                                            <TableCell component="th" scope="row">{d.facTimeText}</TableCell>
                                                                        ) : d.isFirstOfBulk && (
                                                                            <TableCell rowSpan={d.span} component="th" scope="row">{d.facTimeText}</TableCell>
                                                                        )}
                                                                        {d.isFirstOfGtnRun && (
                                                                            <TableCell rowSpan={d.gtnSpan} component="th" scope="row">{d.gtnCellText}</TableCell>
                                                                        )}
                                                                        {d.isFirstOfBulk && (
                                                                            <TableCell rowSpan={d.span} component="th" scope="row">{row.remark}</TableCell>
                                                                        )}
                                                                    </TableRow>
                                                                );
                                                            })}
                                                        </TableBody>
                                                        <TableHead>
                                                            <TableRow>
                                                                <TableCell colSpan={4} align="center" style={{ fontWeight: "bold" }}>Total</TableCell>
                                                                <TableCell align="right" style={{ fontWeight: "bold" }}>{totals.fieldBagCount}</TableCell>
                                                                <TableCell align="right" style={{ fontWeight: "bold" }}>{parseFloat(totals.origFieldWt).toFixed(2)}</TableCell>
                                                                <TableCell />
                                                                <TableCell colSpan={4} />
                                                                <TableCell align="right" style={{ fontWeight: "bold" }}>{totals.facBagCount}</TableCell>
                                                                <TableCell align="right" style={{ fontWeight: "bold" }}>{parseFloat(totals.origFacWt).toFixed(2)}</TableCell>
                                                                <TableCell colSpan={3} />
                                                            </TableRow>
                                                        </TableHead>
                                                    </Table>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="pt-2">
                                        <input
                                            type="button"
                                            className="btn btn-primary pull-right ml-2"
                                            value="Excel"
                                            onClick={createFile}
                                        />
                                        <ReactToPrint
                                            documentTitle={"QR Tag Bag Wise Report"}
                                            trigger={() => <input
                                                type="submit"
                                                className="btn btn-primary pull-right ml-2"
                                                value="PDF"
                                            />}
                                            content={() => componentRef.current}
                                        />
                                    </div>
                                    <div hidden={true}>
                                        <CreatePdf ref={componentRef}
                                            reportData={sortedReportData}
                                            searchData={selectedSearchValues} />
                                    </div>
                                </div>
                            }
                        </div>
                    </div>
                </div>
            </div>
            <MessageModal
                hidden={messageModel.hidden}
                messageType={messageModel.type}
                messageDescription={messageModel.description}
                clicked={modalDismissHandler}
            />
        </Fragment>
    );
}